import React, { useState, useEffect } from 'react';
import { Button, Input, Modal, Table, EmptyState } from '../../components';
import { generateMentorInvite, getMyInvites, revokeInvite } from '../../services/invite-service';
import { getUsers } from '../../services/user-service';

interface MentorInvite {
  id: string;
  name: string;
  email: string;
  status: 'invited' | 'active';
  inviteDate: string;
  inviteLink?: string;
}

const MentorManager: React.FC = () => {
  const [mentors, setMentors] = useState<MentorInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const [invites, users] = await Promise.all([getMyInvites(), getUsers('mentor')]);
        const inviteList: MentorInvite[] = invites
          .filter((inv: any) => inv.role === 'mentor')
          .map((inv: any) => ({
            id: inv._id,
            name: inv.name || inv.email,
            email: inv.email,
            status: inv.usedAt ? 'active' as const : 'invited' as const,
            inviteDate: inv.createdAt,
            inviteLink: inv.usedAt ? undefined : `${window.location.origin}/invite/${inv.token}`,
          }));
        const userList: MentorInvite[] = users
          .filter((u: any) => !inviteList.some(inv => inv.email === u.email))
          .map((u: any) => ({
            id: u._id,
            name: u.name,
            email: u.email,
            status: 'active' as const,
            inviteDate: u.createdAt,
          }));
        setMentors([...inviteList, ...userList]);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  const handleGenerateInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      const result = await generateMentorInvite(inviteEmail, inviteName);
      const link = result.link;
      setGeneratedLink(link);

      const newInvite: MentorInvite = {
        id: result.invite._id,
        name: inviteName,
        email: inviteEmail,
        status: 'invited',
        inviteDate: new Date().toISOString(),
        inviteLink: link,
      };
      setMentors(prev => [newInvite, ...prev]);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = (link: string, id: string) => {
    navigator.clipboard.writeText(link);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCancelInvite = async (id: string) => {
    if (!window.confirm('Cancel this invite? The invite link will stop working.')) return;
    try {
      await revokeInvite(id);
      setMentors(prev => prev.filter(m => m.id !== id));
    } catch (e) { console.error(e); }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setInviteName('');
    setInviteEmail('');
    setGeneratedLink('');
  };

  const stats = [
    { label: 'Total Mentors', value: mentors.length },
    { label: 'Active', value: mentors.filter(m => m.status === 'active').length },
    { label: 'Invited', value: mentors.filter(m => m.status === 'invited').length },
  ];

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value === 'active'
              ? 'bg-success/15 text-success'
              : 'bg-warning/15 text-warning'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'inviteDate',
      label: 'Invite Date',
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: '',
      render: (_: any, row: MentorInvite) =>
        row.status === 'invited' ? (
          <div className="flex items-center gap-2">
            {row.inviteLink && (
              <Button variant="ghost" size="sm" onClick={() => handleCopyLink(row.inviteLink!, row.id)}>
                {copied === row.id ? 'Copied!' : 'Copy Link'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => handleCancelInvite(row.id)}
              className="text-danger hover:text-danger/70">
              Cancel
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-text">Mentor Management</h1>
          <p className="text-muted">Invite and manage mentors on the platform</p>
        </div>
        <Button variant="primary" onClick={() => setModalOpen(true)}>
          Generate Invite Link
        </Button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-x-8 pb-6 border-b border-line">
        {stats.map((s, i, arr) => (
          <div key={s.label} className={`flex flex-col ${i < arr.length - 1 ? 'pr-8 border-r border-line' : ''}`}>
            <span className="text-2xl font-bold text-text">{s.value}</span>
            <span className="text-xs text-muted mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Mentor Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      ) : mentors.length === 0 ? (
        <EmptyState
          title="No mentors yet"
          description="Generate an invite link to bring mentors onto the platform."
        />
      ) : (
        <Table columns={columns} data={mentors} />
      )}

      {/* Invite Modal */}
      <Modal isOpen={modalOpen} onClose={handleCloseModal} title="Invite Mentor">
        {!generatedLink ? (
          <div className="space-y-4">
            <Input
              label="Full Name"
              placeholder="Enter mentor's name"
              value={inviteName}
              onChange={e => setInviteName(e.target.value)}
              required
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter mentor's email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              required
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerateInvite}
                disabled={submitting || !inviteName.trim() || !inviteEmail.trim()}
              >
                {submitting ? 'Generating...' : 'Generate Link'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Invite link generated for <span className="text-text font-medium">{inviteName}</span>. Share
              this link with the mentor:
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={generatedLink}
                className="flex-1 bg-shell border border-line rounded-lg px-3 py-2 text-text text-sm truncate"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleCopyLink(generatedLink, 'modal')}
              >
                {copied === 'modal' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="ghost" onClick={handleCloseModal}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MentorManager;
